// src/oura/overview/NextCardFrame.tsx
//
// The Next card's frame, shared by the Overview card (HubNextCard) and the
// Onboarding page's own card, so the two are drawn by one piece of code:
// label "Next" (or "Done"), one tag, a title, the recipients and why lines,
// the action slot, anything under it (the payment row, a refusal), and the
// blue bar along the foot while a check runs.

import type { ReactNode } from "react";
import { CheckCircle2Icon } from "lucide-react";
import { Surface, Tag, TYPE, type Tone } from "@oura/ui";
import { cn } from "../lib/cn";

export interface NextCardFrameProps {
    complete: boolean;
    tag: { label: string; tone: Tone } | null;
    title: string;
    /** Who a release goes to: what staff check before they open the drawer. Empty: no line. */
    recipients?: string;
    why: string;
    /** The buttons, beside the words. */
    action?: ReactNode;
    /** Under the words: a form or a refusal. */
    children?: ReactNode;
    /** 0 to 100 while a check runs. Null: no bar. */
    percent: number | null;
    /** The progress bar's name. */
    progressLabel: string;
    /**
     * Merchant: no "Next" label over the title, and the tag sits beside the
     * title instead. The merchant's pages say what is next in the title
     * itself; the label only repeated it.
     */
    inlineTag?: boolean;
}

export function NextCardFrame({ complete, tag, title, recipients, why, action, children, percent, progressLabel, inlineTag = false }: NextCardFrameProps) {
    return (
        <Surface className="relative overflow-hidden">
            <div data-next-card="" tabIndex={-1} className="flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg outline-none">
                <div className="min-w-0 flex-1 basis-64">
                    {!inlineTag && (
                        <div className="mb-1 flex flex-wrap items-center gap-2">
                            <span className={TYPE.lab}>{complete ? "Done" : "Next"}</span>
                            {!complete && tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
                        </div>
                    )}
                    <h2 className={cn(TYPE.tt, "m-0 flex flex-wrap items-center gap-x-2 gap-y-1 text-gray-900")}>
                        {complete && <CheckCircle2Icon aria-hidden size={20} className="shrink-0 text-emerald-500" />}
                        {title}
                        {inlineTag && !complete && tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
                    </h2>
                    {recipients && <p data-release-recipient="" className={cn(TYPE.tc, "m-0 mt-0.5 font-medium text-gray-700")}>{recipients}</p>}
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{why}</p>
                </div>
                {action}
            </div>
            {children}
            {percent !== null && (
                <div
                    role="progressbar"
                    aria-label={progressLabel}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={percent}
                    // Along the card's bottom edge: the card keeps its height as the check starts and ends.
                    className="absolute inset-x-0 bottom-0 h-1 bg-gray-100"
                >
                    <div className="h-full bg-blue-600 transition-[width] duration-700 motion-reduce:transition-none" style={{ width: `${percent}%` }} />
                </div>
            )}
        </Surface>
    );
}
